/**
 * Helpers Supabase pour les espèces.
 * Centralisé ici pour garder les pages courtes et cohérentes.
 */

import { supabase } from './supabase.js';
import { t, getLang } from './i18n.js';

/**
 * Liste les espèces, avec filtres optionnels.
 * Recherche sur common_name_fr, common_name_en et scientific_name.
 *
 * @param {object} opts
 * @param {string}  [opts.search]      - filtre texte
 * @param {boolean} [opts.dangerOnly]  - n'afficher que les espèces dangereuses
 * @param {string}  [opts.zoneId]      - UUID de la région, filtre via species_regions
 * @param {string}  [opts.category]    - catégorie taxonomique (fish, mollusk, coral…)
 */
export async function listSpecies({ search = '', dangerOnly = false, zoneId = null, category = null } = {}) {
  // Si une zone est demandée, récupérer d'abord les IDs d'espèces de cette zone
  let zoneSpeciesIds = null;
  if (zoneId) {
    const { data: srData, error: srError } = await supabase
      .from('species_regions')
      .select('species_id')
      .eq('region_id', zoneId);
    if (srError) throw srError;
    zoneSpeciesIds = (srData ?? []).map(r => r.species_id);
    if (zoneSpeciesIds.length === 0) return [];
  }

  let query = supabase
    .from('species')
    .select(
      'id, scientific_name, common_name_fr, common_name_en, max_size_cm, danger_level, conservation_status, primary_image_url, is_invasive'
    )
    .order('common_name_fr', { ascending: true });

  if (zoneSpeciesIds !== null) {
    query = query.in('id', zoneSpeciesIds);
  }

  if (category) {
    query = query.eq('category', category);
  }

  if (search.trim()) {
    const s = search.trim().replace(/[%_]/g, '');
    query = query.or(
      `common_name_fr.ilike.%${s}%,common_name_en.ilike.%${s}%,scientific_name.ilike.%${s}%`
    );
  }

  if (dangerOnly) {
    query = query.gt('danger_level', 0);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}

/**
 * Récupère une espèce avec ses images.
 *
 * @param {string} id - UUID de l'espèce
 */
export async function getSpecies(id) {
  const { data, error } = await supabase
    .from('species')
    .select(
      `*,
       species_images ( url, thumbnail_url, source, source_url, license, attribution, is_primary )`
    )
    .eq('id', id)
    .single();
  if (error) throw error;
  return data;
}

// ---------- Helpers de présentation ----------

/**
 * Retourne le nom d'affichage d'une espèce selon la langue courante.
 */
export function displayName(sp) {
  if (getLang() === 'en' && sp.common_name_en) return sp.common_name_en;
  return sp.common_name_fr || sp.scientific_name;
}

/**
 * Retourne la valeur d'un champ traduit (ex: 'habitat') selon la langue courante.
 * Fallback sur le champ FR si la version EN est absente.
 */
export function pickField(species, field) {
  if (getLang() === 'en') {
    const en = species[`${field}_en`];
    if (en) return en;
  }
  return species[field] ?? null;
}

/**
 * Retourne le libellé de niveau de danger (0–5) dans la langue courante.
 */
export function dangerLabel(level) {
  return t(`danger.${level}`);
}

/**
 * Retourne le libellé du statut UICN dans la langue courante.
 */
export function iucnLabel(code) {
  return t(`iucn.${code}`);
}

/**
 * Formate une plage de profondeur dans la langue courante.
 */
export function formatDepthRange(min, max) {
  if (min == null && max == null) return null;
  if (min != null && max != null) return `${min}–${max} m`;
  if (max != null) return t('depth.upTo', { max });
  return t('depth.from', { min });
}

/**
 * Formate une taille en cm ou m (au-delà de 100 cm).
 */
export function formatSize(cm) {
  if (!cm) return null;
  if (cm >= 100) return `${(cm / 100).toFixed(cm % 100 === 0 ? 0 : 1)} m`;
  return `${cm} cm`;
}

/**
 * Retourne N espèces aléatoires avec leur zone, pour la section "Découvrir".
 * Dédoublonne par species_id (une espèce présente dans deux zones ne sort qu'une fois).
 *
 * @param {object} opts
 * @param {string}  [opts.zoneId] - filtre sur une zone précise (null = toutes zones)
 * @param {number}  [opts.limit]  - nombre d'espèces à retourner
 */
export async function listFeaturedSpecies({ zoneId = null, limit = 5 } = {}) {
  let query = supabase
    .from('species_regions')
    .select(`
      regions:region_id ( id, slug, name ),
      species:species_id ( id, scientific_name, common_name_fr, common_name_en, primary_image_url )
    `);

  if (zoneId) {
    query = query.eq('region_id', zoneId);
  }

  const { data, error } = await query;
  if (error) throw error;

  // Dédoublonnage par species_id (une espèce peut exister dans plusieurs zones)
  const seen = new Map();
  for (const row of data ?? []) {
    if (row.species && !seen.has(row.species.id)) {
      // Normalise name → name_fr pour compatibilité avec zoneName()
      const zone = { ...row.regions, name_fr: row.regions?.name, name_en: null };
      seen.set(row.species.id, { species: row.species, zone });
    }
  }

  // Fisher-Yates shuffle
  const items = [...seen.values()];
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [items[i], items[j]] = [items[j], items[i]];
  }

  return items.slice(0, limit);
}
